// src/services/caseService.js
import axiosInstance from "../config/axiosInstance";

const BASE_URL = "/cases";

/**
 * Obtiene una lista paginada de casos con búsqueda opcional.
 * @param {number} page - Número de página (1..N)
 * @param {number} size - Tamaño de página
 * @param {string|null} searchTerm - Término de búsqueda (opcional)
 * @returns {Promise<ApiResponse<PaginatedResult<CaseListItemDto>>>}
 */
export const getCasesPaged = async (page = 1, size = 10, searchTerm = null) => {
  const { data } = await axiosInstance.get(
    `${BASE_URL}/paged?pageIndex=${page}&pageSize=${size}&searchTerm=${
      searchTerm ?? ""
    }`
  );
  return data;
};
/**
 * Obtiene una lista paginada de casos EXCLUSIVAMENTE
 * asignados al abogado autenticado (Mis casos).
 *
 * Este endpoint debe mapear al método:
 *   [GET] /Cases/my-paged
 * en tu API (CaseController).
 *
 * @param {number} page - Número de página (1..N)
 * @param {number} size - Tamaño de página
 * @param {string|null} searchTerm - Término de búsqueda (opcional)
 * @returns {Promise<ApiResponse<PaginatedResult<CaseListItemDto>>>}
 */
export const getMyCasesPaged = async (
  page = 1,
  size = 10,
  searchTerm = null
) => {
  const { data } = await axiosInstance.get(
    `${BASE_URL}/my-paged?pageIndex=${page}&pageSize=${size}&searchTerm=${
      searchTerm ?? ""
    }`
  );
  return data;
};
/**
 * Obtiene el detalle de un caso por su ID.
 * @param {string} id - ID del caso (GUID)
 * @returns {Promise<ApiResponse<CaseDetailDto>>}
 */
export const getCaseById = async (id) => {
  const { data } = await axiosInstance.get(`${BASE_URL}/${id}`);
  return data;
};

/**
 * Crea un nuevo caso legal.
 * @param {CaseCreateDto} payload - Datos para crear el caso
 * @returns {Promise<ApiResponse<string>>} // Guid
 */
export const createCase = async (payload) => {
  const { data } = await axiosInstance.post(BASE_URL, payload);
  return data;
};

/**
 * Actualiza un caso legal existente.
 * @param {string} id - ID del caso
 * @param {CaseUpdateDto} payload - Datos a actualizar
 * @returns {Promise<ApiResponse<boolean>>}
 */
export const updateCase = async (id, payload) => {
  const { data } = await axiosInstance.put(`${BASE_URL}/${id}`, payload);
  return data;
};

/**
 * Realiza un "soft delete" de un caso (marca como eliminado lógicamente).
 * @param {string} id - ID del caso
 * @returns {Promise<ApiResponse<boolean>>}
 */
export const deleteCase = async (id) => {
  const { data } = await axiosInstance.delete(`${BASE_URL}/${id}`);
  return data;
};

/**
 * Obtiene una lista paginada de casos eliminados lógicamente,
 * con opción de búsqueda por nombre, número de caso, cliente o abogado.
 * @param {number} page - Número de página (1..N)
 * @param {number} size - Tamaño de página
 * @param {string|null} searchTerm - Término de búsqueda (opcional)
 * @returns {Promise<ApiResponse<PaginatedResult<CaseDeletedListItemDto>>>}
 */
export const getDeletedCasesPaged = async (
  page = 1,
  size = 10,
  searchTerm = null
) => {
  const { data } = await axiosInstance.get(
    `${BASE_URL}/deleted?pageIndex=${page}&pageSize=${size}&searchTerm=${
      searchTerm ?? ""
    }`
  );
  return data;
};

/**
 * Restaura un caso que fue eliminado lógicamente.
 * @param {string} id - Guid del caso
 * @returns {Promise<ApiResponse<boolean>>}
 */
export const restoreCase = async (id) => {
  const { data } = await axiosInstance.put(`${BASE_URL}/${id}/restore`);
  return data;
};

/**
 * Obtiene el timeline (historial de eventos) de un caso.
 * @param {string} caseId - Id del caso
 * @returns {Promise<ApiResponse<CaseTimelineItemDto[]>>}
 */
export const getCaseTimeline = async (caseId) => {
  const { data } = await axiosInstance.get(`${BASE_URL}/${caseId}/timeline`);
  return data; // ApiResponse<IReadOnlyList<CaseTimelineItemDto>>
};

/**
 * Genera y obtiene el reporte PDF de un caso.
 * @param {string} caseId - Id del caso
 * @returns {Promise<ApiResponse<Blob>>}
 */
export const getCaseReport = async (caseId) => {
  const { data } = await axiosInstance.get(`${BASE_URL}/${caseId}/get-report`);
  return data;
};
